import React, { useState } from 'react';
import { X, Calendar, Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { createAppointment } from '@/services/appointments';

interface DoctorScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: string;
  patientName: string;
  doctorId: string;
}

export const DoctorScheduleModal: React.FC<DoctorScheduleModalProps> = ({
  isOpen, onClose, patientId, patientName, doctorId
}) => {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [type, setType] = useState<'video' | 'clinic' | 'home'>('video');
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!date || !time) {
      toast.error('Preencha data e horário');
      return;
    }
    
    setIsLoading(true);
    try {
      await createAppointment({
        patientId,
        doctorId,
        date,
        time,
        type,
        reason: 'Retorno agendado pelo médico',
        appointmentType: type === 'video' ? 'TELEMEDICINA' : type === 'home' ? 'DOMICILIAR' : 'CONSULTORIO'
      });
      toast.success('Retorno agendado com sucesso!');
      onClose();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erro ao agendar retorno');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-white dark:bg-neutral-900 rounded-3xl w-full max-w-md overflow-hidden shadow-2xl border border-neutral-200 dark:border-neutral-800">
        <div className="flex items-center justify-between p-6 border-b border-neutral-200 dark:border-neutral-800">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Agendar Retorno</h2>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:bg-neutral-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Agendando para o paciente: <strong className="text-gray-900 dark:text-white">{patientName}</strong>
          </p>
          
          <div className="flex gap-4">
            <div className="flex-1 space-y-1.5">
              <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Data</label>
              <input type="date" required value={date} onChange={e => setDate(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-transparent" />
            </div>
            <div className="flex-1 space-y-1.5">
              <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Horário</label>
              <input type="time" required value={time} onChange={e => setTime(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-transparent" />
            </div>
          </div>
          
          <div className="space-y-1.5">
            <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Modalidade</label>
            <select value={type} onChange={(e: any) => setType(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-transparent">
              <option value="video">Telemedicina</option>
              <option value="clinic">Consultório</option>
              <option value="home">Domiciliar</option>
            </select>
          </div>

          <div className="pt-4 flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 py-2 rounded-xl border font-bold text-gray-600 hover:bg-neutral-50">Cancelar</button>
            <button type="submit" disabled={isLoading} className="flex-1 py-2 rounded-xl bg-teal-600 text-white font-bold hover:bg-teal-700 disabled:opacity-50 flex justify-center items-center gap-2">
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
